import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Mail, Phone, MapPin } from "lucide-react";
import api, { formatError } from "@/lib/api";
import { useFlags } from "@/hooks/useFlags";
import { Button } from "@/components/ui/button";

export function PartnerRequestsPanel() {
  const { t } = useTranslation();
  const v4 = useFlags().v4_enabled;
  const [reqs, setReqs] = useState([]);
  const load = () => api.get("/admin/partner-requests").then((r) => setReqs(r.data.requests)).catch(() => {});
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);
  const setStatus = async (id, status) => {
    try { await api.patch(`/admin/partner-requests/${id}`, { status }); load(); }
    catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  };
  const approve = async (id) => {
    try {
      const r = await api.post(`/admin/partner-requests/${id}/approve`);
      load();
      toast.success(`${t("partnerAdmin.approved")} — ${t("partnerAdmin.tempPwd")}: ${r.data.temp_password}`, { duration: 15000 });
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  };
  const exportCsv = async () => {
    try {
      const res = await api.get("/admin/partner-requests/export.csv", { responseType: "blob" });
      const url = URL.createObjectURL(res.data); const a = document.createElement("a");
      a.href = url; a.download = "partner-requests.csv"; a.click(); URL.revokeObjectURL(url);
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  };
  const badge = { new: "bg-canard/15 text-canard-dark", contacted: "bg-yellow-100 text-yellow-700", closed: "bg-gray-100 text-gray-500", approved: "bg-emeraude/15 text-emeraude-dark" };
  return (
    <div data-testid="partner-requests-panel" className="space-y-3">
      <div className="flex justify-end">
        <Button size="sm" variant="outline" data-testid="export-partners-csv" onClick={exportCsv} className="rounded-full h-8 text-xs">Export CSV</Button>
      </div>
      {reqs.length === 0 ? (
        <p className="text-muted2 font-body text-center py-10 bg-white rounded-2xl border border-border" data-testid="partner-requests-empty">{t("partnerAdmin.empty")}</p>
      ) : reqs.map((r) => (
        <div key={r.id} data-testid={`partner-request-${r.id}`} className="bg-white rounded-2xl border border-border p-5">
          <div className="flex items-start justify-between gap-2">
            <div>
              <p className="font-heading font-bold text-ink">{r.name} <span className="text-xs font-normal text-muted2">· {r.type}</span></p>
              <p className="text-sm text-muted2 mt-1 flex flex-wrap gap-3">
                <span className="flex items-center gap-1"><Mail className="w-3.5 h-3.5" /> {r.email}</span>
                {r.phone && <span className="flex items-center gap-1"><Phone className="w-3.5 h-3.5" /> {r.phone}</span>}
                {r.island && <span className="flex items-center gap-1"><MapPin className="w-3.5 h-3.5" /> {r.island}</span>}
              </p>
            </div>
            <span className={`text-xs font-bold px-2 py-0.5 rounded-full shrink-0 ${badge[r.status] || badge.new}`}>{t(`partnerAdmin.${r.status}`)}</span>
          </div>
          {r.description && <p className="text-sm text-ink font-body mt-2 whitespace-pre-line">{r.description}</p>}
          <div className="flex gap-2 mt-3">
            <Button size="sm" variant="outline" data-testid={`preq-contacted-${r.id}`} onClick={() => setStatus(r.id, "contacted")} className="rounded-full h-8 text-xs">{t("partnerAdmin.markContacted")}</Button>
            <Button size="sm" variant="outline" data-testid={`preq-closed-${r.id}`} onClick={() => setStatus(r.id, "closed")} className="rounded-full h-8 text-xs">{t("partnerAdmin.markClosed")}</Button>
            {v4 && r.status !== "approved" && !r.account_id && (
              <Button size="sm" data-testid={`preq-approve-${r.id}`} onClick={() => approve(r.id)} className="rounded-full h-8 text-xs bg-emeraude hover:bg-emeraude-dark font-bold">{t("partnerAdmin.approve")}</Button>
            )}
          </div>
        </div>
      ))}
    </div>
  );
}
